import * as v from 'valibot';
import { ACHIEVEMENT_IDS } from './achievements';
import {
  ABILITY_IDS,
  AURA_IDS,
  CLASS_IDS,
  CLASS_SLOTS,
  UTILITY_SLOTS,
} from './classes';
import { EMOTE_IDS, type Emote } from './hub';
import type {
  AbilityId,
  AuraId,
  ClassId,
  ClassPair,
  ResourceType,
  School,
  ZoneId,
} from './classes';
import type { ArenaMap } from './map';
import type { MoveControl, MoveState } from './movement';

export type Bracket = '1v1' | '2v2' | '3v3';
export const BRACKETS: Bracket[] = ['1v1', '2v2', '3v3'];
export const bracketSize = (bracket: Bracket): number => Number(bracket[0]);

// ---------------------------------------------------------------------------------------------
// Match state
// ---------------------------------------------------------------------------------------------

export type Phase = 'prep' | 'live' | 'ended';

export interface AuraSnap {
  auraId: AuraId;
  sourceId: string;
  /** Seconds left; 0 when it lasts until removed. */
  remaining: number;
  duration: number;
  absorb?: number;
}

export interface CastSnap {
  abilityId: AbilityId;
  targetId: string | null;
  elapsed: number;
  duration: number;
  channel: boolean;
  /** Ground abilities: where it lands. */
  point?: [number, number, number];
}

/** Hidden zones only reach their owner's team. */
export interface ZoneSnap {
  id: number;
  zoneId: ZoneId;
  team: 0 | 1;
  pos: [number, number, number];
  armed: boolean;
}

export interface UnitSnap {
  id: string;
  name: string;
  team: 0 | 1;
  classes: ClassPair;
  /** Which of `classes` it's playing. */
  active: 0 | 1;
  dummy: boolean;
  /** A totem: drawn as one, never in unit frames. */
  totem?: ZoneId;
  pos: [number, number, number];
  yaw: number;
  vel: [number, number, number];
  grounded: boolean;
  hp: number;
  maxHp: number;
  /** One pool per class, in `classes` order. */
  resources: [number, number];
  targetId: string | null;
  cast: CastSnap | null;
  auras: AuraSnap[];
  dead: boolean;
  /** A released ally walking as a spirit; enemies only ever see its body. */
  ghost?: boolean;
  stealthed: boolean;
  /** Hub only: a line under the nameplate, like an NPC's title or a player's queue. */
  tag?: string;
  emote?: Emote;
  /** Hub only: the classes whose weapon trails they wear in each hand; a match's come in the `roster`. */
  trail?: ClassId;
  offTrail?: ClassId;
}

/** State only the controlling player sees about their own unit. */
export interface SelfSnap {
  id: string;
  /** Last input the server has applied; the client replays everything after it. */
  ack: number;
  move: MoveState;
  control: MoveControl;
  combo: number;
  gcd: { remaining: number; duration: number };
  cooldowns: Partial<
    Record<AbilityId, { remaining: number; duration: number }>
  >;
  /** Times each ability with `uses` has been used. */
  used: Partial<Record<AbilityId, number>>;
  lockouts: Partial<Record<School, number>>;
  queued: AbilityId | null;
  /** Until the next auto-attack is ready, while auto-attack is on; a shot's `aim` comes after and isn't counted. */
  swing: { remaining: number; duration: number } | null;
}

export interface Snapshot {
  tick: number;
  phase: Phase;
  /** Seconds until the gates open (prep) or the match times out (live). */
  phaseRemaining: number;
  /** Fraction healing is reduced by. */
  dampening: number;
  units: UnitSnap[];
  zones: ZoneSnap[];
  self: SelfSnap | null;
}

/** A hub unit: just enough to draw and interpolate it, since the hub has no combat. */
export interface HubUnit {
  id: string;
  name: string;
  /** The class it's drawn as: a player's first. */
  classId: ClassId;
  pos: [number, number, number];
  yaw: number;
  vel: [number, number, number];
  grounded: boolean;
  npc?: boolean;
  tag?: string;
  emote?: Emote;
  trail?: ClassId;
  offTrail?: ClassId;
}

export type CombatEvent =
  | {
      type: 'damage';
      source: string | null;
      target: string;
      amount: number;
      absorbed: number;
      school: School;
      abilityId: AbilityId | 'melee' | null;
      /** Periodic ticks: the aura ticking. */
      auraId?: AuraId;
    }
  | {
      type: 'heal';
      source: string | null;
      target: string;
      amount: number;
      overheal: number;
      abilityId: AbilityId | null;
      auraId?: AuraId;
    }
  /** Damage a shield soaked, credited to whoever cast the shield. */
  | {
      type: 'absorb';
      source: string | null;
      target: string;
      amount: number;
      auraId: AuraId;
    }
  /** `dr`: diminishing returns, not an immunity, turned the aura away. */
  | { type: 'immune'; source: string | null; target: string; dr?: true }
  /** Resource an ability gave, after the cap. */
  | {
      type: 'energize';
      source: string;
      target: string;
      amount: number;
      resource: ResourceType;
    }
  /** A `cheatDeath` aura spent itself to keep `target` alive through `source`'s blow. */
  | {
      type: 'cheatDeath';
      source: string | null;
      target: string;
      auraId: AuraId;
    }
  /** An effect finished `source`'s cooldown early. Only `source` gets it. */
  | { type: 'reset'; source: string; abilityId: AbilityId }
  /** `source` pulled `target` out of stealth. */
  | { type: 'revealed'; source: string; target: string }
  | {
      type: 'castStart';
      source: string;
      target: string | null;
      abilityId: AbilityId;
      duration: number;
      point?: [number, number, number];
    }
  | {
      type: 'castSuccess';
      source: string;
      target: string | null;
      abilityId: AbilityId;
      travel: number;
      point?: [number, number, number];
    }
  /** `source` reflected `target`'s spell back at them. */
  | { type: 'reflect'; source: string; target: string; abilityId: AbilityId }
  /** A grounding zone swallowed `source`'s spell before it reached `target`. */
  | { type: 'grounded'; source: string; target: string; abilityId: AbilityId }
  /** A chain leapt from `source` to `target`; the hit itself follows as its own event. */
  | { type: 'chain'; source: string; target: string; abilityId: AbilityId }
  /** `source`'s trap went off at `point`; what it does to whoever set it off follows as its own events. */
  | {
      type: 'sprung';
      source: string;
      abilityId: AbilityId;
      point: [number, number, number];
    }
  | {
      type: 'castStop';
      source: string;
      abilityId: AbilityId;
      reason:
        'interrupted' | 'damage' | 'moved' | 'cancelled' | 'cc' | 'target';
    }
  | {
      type: 'interrupt';
      source: string;
      target: string;
      school: School;
      duration: number;
    }
  | {
      type: 'auraApplied';
      source: string | null;
      target: string;
      auraId: AuraId;
    }
  | {
      type: 'auraRemoved';
      target: string;
      auraId: AuraId;
      reason: 'expired' | 'dispelled' | 'broken' | 'cancelled';
      /** Who dispelled it. */
      source?: string;
    }
  | { type: 'death'; target: string; killer: string | null }
  | { type: 'error'; target: string; message: string };

export interface RosterUnit {
  id: string;
  name: string;
  classes: ClassPair;
  trail?: ClassId;
  offTrail?: ClassId;
}

export interface MatchSummaryUnit {
  id: string;
  name: string;
  team: 0 | 1;
  classes: ClassPair;
  damage: number;
  healing: number;
  ratingBefore?: number;
  ratingAfter?: number;
}

// ---------------------------------------------------------------------------------------------
// Lobby
// ---------------------------------------------------------------------------------------------

/** Two full 3v3 sides, so a party doubles as a private lobby. */
export const MAX_PARTY = 6;

/** Why `name` can't be a Discord player's name, or null if it can; the lobby also checks no one else has it. Guests are `Guest1234`. */
export function nameProblem(name: string): string | null {
  if (name.length < 3 || name.length > 24)
    return 'Names are 3 to 24 characters long.';
  if (!/^[A-Za-z]+(?:'[A-Za-z]+)*$/.test(name))
    return 'Names use only letters, with apostrophes allowed between them.';
  const letters = name.toLowerCase().replaceAll("'", '');
  if (BLOCKED_NAME_PARTS.some((part) => letters.includes(part)))
    return "That name isn't allowed.";
  return null;
}

/** Slurs and obscenities no name may contain. Matched anywhere in the name, so only parts that can't hide inside ordinary words. */
const BLOCKED_NAME_PARTS = [
  'fuck',
  'shit',
  'cunt',
  'nigg',
  'fagg',
  'retard',
  'whore',
  'slut',
  'bitch',
  'penis',
  'vagina',
  'rapist',
  'hitler',
  'kike',
  'tranny',
  'molest',
];

export interface PartyMember {
  id: string;
  name: string;
  classes: ClassPair;
  /** Which side they fight on in a private match. */
  team: 0 | 1;
  /** Name of the map they're practicing on, which the rest of the party can join. */
  practice: string | null;
}

export interface PartyInfo {
  leaderId: string;
  members: PartyMember[];
}

export interface Rating {
  bracket: Bracket;
  rating: number;
  wins: number;
  losses: number;
}

/** A leaderboard window keeps players who played a rated match in it, ranked by current rating. */
export type LeaderboardWindow = 'week' | 'month' | 'all';
export const LEADERBOARD_WINDOWS: LeaderboardWindow[] = [
  'week',
  'month',
  'all',
];

export interface LeaderboardRow {
  id: string;
  name: string;
  /** The classes of their latest match in the bracket. */
  classes: ClassId[];
  rating: number;
  wins: number;
  losses: number;
}

export interface AchievementProgress {
  /** When each earned achievement was earned, by id. */
  earned: Record<string, number>;
  /** Lifetime stats from rated matches, which achievements count toward. */
  stats: Record<string, number>;
  /** The achievement whose title they show. */
  title: string | null;
  /** The achievements whose weapon trails they wear in the main and off hand. */
  trail: string | null;
  offTrail: string | null;
  /** Outside production, and for admins, any trail can be worn unearned. */
  everyTrail?: boolean;
}

export const CHAT_CHANNELS = ['say', 'yell', 'party'] as const;
export type ChatChannel = (typeof CHAT_CHANNELS)[number];

export interface ChatLine {
  channel: ChatChannel | 'system' | 'whisper';
  /** Absent on system lines. */
  from?: string;
  fromId?: string;
  /** Whispers only: set on the sender's own copy, naming who it went to. */
  to?: string;
  text: string;
  at: number;
}

export type ServerMessage =
  | {
      t: 'welcome';
      user: { id: string; name: string; guest: boolean };
      classes: ClassPair;
      ratings: Rating[];
      /** The commit the lobby was built from ('' in development); a client built from another reloads. */
      build: string;
    }
  | {
      t: 'lobby';
      online: number;
      queued: Record<Bracket, number>;
      /** Players in live matches. */
      fighting: number;
    }
  /** Echoes a `ping` on whichever socket it came in on. */
  | { t: 'pong'; at: number }
  | { t: 'chat'; line: ChatLine }
  | { t: 'party'; party: PartyInfo | null }
  | { t: 'partyInvite'; from: string }
  /** `team`: a challenge to our whole party, sent to its leader. */
  | { t: 'duelRequest'; from: string; team: boolean }
  | { t: 'queue'; bracket: Bracket | null; since: number | null }
  /** The game is full: our place in the line to get in. `welcome` follows once we're in. */
  | { t: 'line'; position: number; size: number }
  | {
      t: 'matchStart';
      matchId: string;
      bracket: Bracket | 'practice' | 'private';
      map: ArenaMap;
      youId: string;
      /** Match server WebSocket URL; a path is relative to the page's host. */
      server: string;
      /** Signed pass the match server checks instead of the auth cookie. */
      ticket: string;
    }
  /** From the match server, on the match socket. */
  | { t: 'snap'; snap: Snapshot; events: CombatEvent[] }
  /** Sent on joining a match: who fights on each side, so arena frames show opponents still in stealth. */
  | {
      t: 'roster';
      teams: [RosterUnit[], RosterUnit[]];
    }
  /** A numbered hub, published to every connected player in it who isn't in a match. */
  | { t: 'hub'; hub: number; tick: number; units: HubUnit[] }
  /** Sent just before each `hub`: the player's own authoritative movement. */
  | { t: 'hubSelf'; tick: number; ack: number; move: MoveState }
  | {
      t: 'matchEnd';
      winner: 0 | 1 | null;
      units: MatchSummaryUnit[];
    }
  | { t: 'ratings'; ratings: Rating[] }
  | { t: 'achievements'; progress: AchievementProgress }
  | { t: 'error'; message: string };

const nameSchema = v.pipe(
  v.string(),
  v.trim(),
  v.minLength(1),
  v.maxLength(64),
);
const finite = v.pipe(v.number(), v.finite());

const chatText = v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(200));

/** Two different classes. */
export const classPairSchema = v.pipe(
  v.tuple([v.picklist(CLASS_IDS), v.picklist(CLASS_IDS)]),
  v.check(([a, b]) => a !== b),
);

/** An arrival, a departure and a discipline; `isTalents` checks they're the class's own. */
export const talentsSchema = v.tuple([
  v.picklist(ABILITY_IDS),
  v.picklist(ABILITY_IDS),
  v.picklist(AURA_IDS),
]);

export const ClientMessageSchema = v.variant('t', [
  v.object({
    t: v.literal('chat'),
    channel: v.picklist(CHAT_CHANNELS),
    text: chatText,
  }),
  v.object({ t: v.literal('whisper'), to: nameSchema, text: chatText }),
  v.object({ t: v.literal('setClasses'), classes: classPairSchema }),
  /** The spells the player brings into their next match as this class. */
  v.object({
    t: v.literal('setBar'),
    classId: v.picklist(CLASS_IDS),
    abilities: v.pipe(
      v.array(v.picklist(ABILITY_IDS)),
      v.maxLength(CLASS_SLOTS),
    ),
  }),
  /** The talents the player brings into their next match as this class. */
  v.object({
    t: v.literal('setTalents'),
    classId: v.picklist(CLASS_IDS),
    talents: talentsSchema,
  }),
  /** The utility spells both their classes share. */
  v.object({
    t: v.literal('setUtility'),
    abilities: v.pipe(
      v.array(v.picklist(ABILITY_IDS)),
      v.maxLength(UTILITY_SLOTS),
    ),
  }),
  v.object({
    t: v.literal('setTitle'),
    achievementId: v.nullable(v.picklist(ACHIEVEMENT_IDS)),
  }),
  v.object({
    t: v.literal('setTrail'),
    hand: v.picklist(['main', 'off']),
    achievementId: v.nullable(v.picklist(ACHIEVEMENT_IDS)),
  }),
  v.object({ t: v.literal('partyInvite'), name: nameSchema }),
  v.object({ t: v.literal('partyAccept') }),
  v.object({ t: v.literal('partyDecline') }),
  v.object({ t: v.literal('partyLeave') }),
  /** Leader only: moves a member to the other side for private matches. */
  v.object({ t: v.literal('partySwapTeam'), memberId: nameSchema }),
  /** Challenges a player to an unrated 1v1, or their party (if not ours) to a team duel. */
  v.object({ t: v.literal('duel'), name: nameSchema }),
  v.object({ t: v.literal('duelAccept') }),
  v.object({ t: v.literal('duelDecline') }),
  v.object({
    t: v.literal('queue'),
    bracket: v.picklist(['1v1', '2v2', '3v3', 'practice', 'private']),
    /** Practice and private only: which map file to fight on; random when absent. */
    map: v.optional(v.pipe(v.string(), v.regex(/^[a-z0-9-]{1,40}$/))),
  }),
  v.object({ t: v.literal('unqueue') }),
  /** Hub only: plays until the player moves, or for its `seconds`. */
  v.object({ t: v.literal('emote'), emote: v.picklist(EMOTE_IDS) }),
  /** Hub only: `/join hub-<n>`; a hub no one is in is made on the spot. */
  v.object({
    t: v.literal('joinHub'),
    hub: v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(9999)),
  }),
  /** Joins the practice a party member is in. */
  v.object({ t: v.literal('joinPractice'), memberId: nameSchema }),
  v.object({
    t: v.literal('input'),
    seq: v.pipe(v.number(), v.integer(), v.minValue(0)),
    forward: finite,
    strafe: finite,
    yaw: finite,
    jump: v.boolean(),
  }),
  v.object({
    t: v.literal('cast'),
    abilityId: nameSchema,
    targetId: v.nullable(nameSchema),
    /** Ground abilities: the spot clicked. */
    point: v.optional(v.tuple([finite, finite, finite])),
  }),
  v.object({ t: v.literal('target'), targetId: v.nullable(nameSchema) }),
  v.object({ t: v.literal('attack'), targetId: nameSchema }),
  v.object({ t: v.literal('stopCast') }),
  v.object({ t: v.literal('cancelAura'), auraId: nameSchema }),
  /** Dead in a match: walk on as a spirit. */
  v.object({ t: v.literal('release') }),
  v.object({ t: v.literal('leaveMatch') }),
  v.object({ t: v.literal('ping'), at: finite }),
  /** Reports an online player to the moderators, with what they said recently. */
  v.object({ t: v.literal('report'), name: nameSchema, reason: chatText }),
  /** Admins only. `duration`: minutes for a mute, days for a ban (0 bans for good). */
  v.object({
    t: v.literal('moderate'),
    action: v.picklist(['mute', 'unmute', 'kick', 'ban', 'unban']),
    name: nameSchema,
    duration: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0))),
    reason: v.optional(chatText),
  }),
]);

export type ClientMessage = v.InferOutput<typeof ClientMessageSchema>;

export function parseClientMessage(raw: string): ClientMessage | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  const result = v.safeParse(ClientMessageSchema, data);
  return result.success ? result.output : null;
}
